import { useState } from 'react';
import { Alert, Col, Collapse, Row } from 'reactstrap';
import {
    formatearPuntaje,
    calcularRiesgoResidualInstitucional,
    diagnosticarRiesgoResidual,
} from '../wizard/utils';
import { TIPO_ACTIVIDAD_VULNERABLE } from '../Utils.global';
import ResultadoEbrHero from './ResultadoEbrHero';
import HistorialCuestionarioTable from './HistorialCuestionarioTable';
import {
    calcularPuntajeRiesgo,
    calcularPromedioEficacia,
    getDisplayNameActividad,
} from './cuestionarioRiesgoDisplay';

function diagnosticar(puntaje) {
    if (puntaje === null) return null;
    if (puntaje < 1.5) return {
        nivel: 'BAJO', color: 'success',
        descripcion: 'El perfil de riesgo inherente es bajo. La entidad opera con prácticas de baja complejidad y exposición limitada.',
    };
    if (puntaje < 2.2) return {
        nivel: 'MEDIO', color: 'warning',
        descripcion: 'El perfil de riesgo inherente es medio. Se recomienda reforzar controles en los factores de mayor peso.',
    };
    return {
        nivel: 'ALTO', color: 'danger',
        descripcion: 'El perfil de riesgo inherente es alto. Se requieren medidas de debida diligencia reforzada (EDD).',
    };
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function DashboardRiesgo({ respuesta, unidadId, onNuevoCuestionario }) {
    const { actividad_vulnerable, cuestionario = [], mitigantes = [], timestamp_utc, usuario, _id } = respuesta;
    const [composicionAbierta, setComposicionAbierta] = useState(false);

    const puntaje = calcularPuntajeRiesgo(cuestionario);
    const diagnostico = diagnosticar(puntaje);

    const promedioEficacia = calcularPromedioEficacia(mitigantes);
    const riesgoResidual = calcularRiesgoResidualInstitucional(puntaje, promedioEficacia);
    const diagnosticoResidual = diagnosticarRiesgoResidual(riesgoResidual);

    const fecha = timestamp_utc
        ? new Date(timestamp_utc).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })
        : '—';

    const unidadIdResuelto = unidadId
        || (typeof respuesta.unidad === 'object' ? respuesta.unidad?._id : respuesta.unidad);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h5 className="mb-0">Diagnóstico de Riesgo Institucional</h5>
                    <small className="text-muted">
                        Actividad: <strong>{getDisplayNameActividad(actividad_vulnerable)}</strong>
                        {' · '}Respondido el {fecha}
                        {usuario?.nombre && <> por <strong>{usuario.nombre}</strong></>}
                    </small>
                </div>
                {onNuevoCuestionario && (
                    <button
                        type="button"
                        className="btn btn-outline-sistema btn-sm"
                        onClick={onNuevoCuestionario}
                    >
                        Actualizar cuestionario
                    </button>
                )}
            </div>

            <ResultadoEbrHero
                nombreActividad={getDisplayNameActividad(actividad_vulnerable)}
                riesgoSectorial={TIPO_ACTIVIDAD_VULNERABLE.find((t) => t.value === actividad_vulnerable)?.riesgo}
                nivelResidual={diagnosticoResidual?.nivel}
            />

            <div className="mb-4 border rounded overflow-hidden">
                <button
                    type="button"
                    className="w-100 text-start btn btn-light border-0 rounded-0 p-3 d-flex justify-content-between align-items-center gap-2"
                    onClick={() => setComposicionAbierta((abierta) => !abierta)}
                    aria-expanded={composicionAbierta}
                    aria-controls="composicion-resultado-ebr"
                    id="composicion-resultado-ebr-toggle"
                >
                    <span className="fw-semibold text-dark">¿De qué se compone mi resultado?</span>
                    <span className="text-muted small" aria-hidden="true">
                        {composicionAbierta ? 'Ocultar' : 'Ver detalle'}
                    </span>
                </button>
                <Collapse isOpen={composicionAbierta}>
                    <div id="composicion-resultado-ebr" className="p-3 border-top bg-white">
                        <div className="mb-4 p-3 border rounded">
                            <h5>Resultado de riesgo inherente</h5>
                            <p className="text-muted small mb-3">
                                Promedio ponderado de las preguntas de frecuencia, escalado en rango 1–3.
                            </p>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Puntaje de riesgo inherente:</strong>{' '}
                                    <span className="fs-5">{formatearPuntaje(puntaje)}</span>
                                    <span className="text-muted small"> / 3.00</span>
                                </Col>
                                <Col md={6}>
                                    <strong>Preguntas respondidas:</strong>{' '}
                                    {cuestionario.length}
                                </Col>
                            </Row>
                            {diagnostico && (
                                <Alert color={diagnostico.color} className="mb-0">
                                    <strong>Diagnóstico — Riesgo {diagnostico.nivel}:</strong>{' '}
                                    {diagnostico.descripcion}
                                </Alert>
                            )}
                        </div>

                        {riesgoResidual !== null && (
                            <div className="mb-0 p-3 border rounded">
                                <h5>Resultado de riesgo residual institucional</h5>
                                <p className="text-muted small mb-3">
                                    Riesgo Residual = Riesgo Inherente × (1 − Promedio de eficacia de controles)
                                </p>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <strong>Promedio de eficacia de controles:</strong>{' '}
                                        {formatearPuntaje(promedioEficacia)}%
                                    </Col>
                                    <Col md={6}>
                                        <strong>Riesgo residual:</strong>{' '}
                                        <span className="fs-5">{formatearPuntaje(riesgoResidual)}</span>
                                    </Col>
                                </Row>
                                {diagnosticoResidual && (
                                    <Alert
                                        color={diagnosticoResidual.nivel === 'BAJO' ? 'success' : 'warning'}
                                        className="mb-0"
                                    >
                                        <strong>Diagnóstico — Riesgo residual {diagnosticoResidual.nivel}:</strong>{' '}
                                        {diagnosticoResidual.descripcion}
                                    </Alert>
                                )}
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>

            <HistorialCuestionarioTable
                unidadId={unidadIdResuelto}
                respuestaActualId={_id}
            />
        </div>
    );
}
